import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';
import SubTitle from 'tags/components/SubTitle.component';
import CustomClientTagContainer from 'tags/CustomClientTagContainer.component';

import fetchSalonTagsList from 'actions/tags/fetchSalonTagsList.action';
import saveSalonTag from 'actions/tags/saveSalonTag.action';
import updateClientTagsList from 'actions/tags/updateClientTagsList.action';
import SalonFlag from 'components/core/SalonFlag.component';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog.component';
import SidebarMenu from 'components/menu/SidebarMenu.component';
import ClientTagsSplashPage from 'components/tags/splash/ClientTagsSplashPage.component';
import LINK from 'constants/link.const';
import SF from 'constants/salonFlag.const';
import { withTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonCalendarMode, selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import { selectClientTagsFromSalon } from 'selectors/simple';
import fromAnyToString from 'utils/converters/fromAnyToString';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import equalStrings from 'utils/strings/equalStrings.util';

import PadLock from 'lib/components/PadLock';
import GeneralTitle from 'settings/page-elements/GeneralTitle';

const L = logger('AddTagsLayout');

class AddTagsLayout extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      alertMessage: '',
      alertToaster: false,
      alertType: '',
      counter: 0,
      menuIsClicked: false,
      unsavedChanges: false,
      unsavedChangesModal: false,
    };
  }

  componentDidMount() {
    const { fetchSalonTagsList, salonId } = this.props;
    L.unignore('componentDidMount()', 'fetchSalonTagsList:', fetchSalonTagsList({ salonId }));
  }

  onClickAddNew = () => {
    const { clientTagsList, salonId, updateClientTagsList } = this.props;
    // Counter for Temp id tag,
    const { counter } = this.state;

    const tempTag = {
      // creating a temporary id since we send the new tag to BE without id, used for react list key
      id: `tempTag+${counter}`,
      salonId,
      name: '',
      emoticon: '',
      description: '',
      number_of_clients: 0,
    };

    updateClientTagsList([...clientTagsList, tempTag]);
    // counter is for temp tags, increments on each new tag implemented, since we can have more than 1 new tag at a time
    // and each tag can be saved, removed individually.
    this.setState({ counter: counter + 1 });
  };

  onExitPage = () => {
    this.setState({
      menuIsClicked: true,
      unsavedChanges: false,
      unsavedChangesModal: false,
    });
  };

  onStayToEdit = () =>
    void this.setState({
      menuIsClicked: false,
      unsavedChanges: true,
      unsavedChangesModal: false,
    });

  setUnsavedChanges = (unsavedChanges) => {
    this.setState({ unsavedChanges });
  };

  handleMenuClick = () => {
    if (!this.state.unsavedChanges) {
      this.setState({ menuIsClicked: !this.state.menuIsClicked, unsavedChangesModal: false });
    } else if (this.state.unsavedChanges) {
      this.setState({ menuIsClicked: false, unsavedChangesModal: true });
    }
  };

  handleBannerClick = () => {
    const { salonId } = this.props;
    navFor('AddTagsLayout.handleBannerClick()', this.props).push(
      linkTo({ pattern: LINK.billing, params: { salonId } }),
    );
  };

  toggleAlert = () =>
    void this.setState({ alertToaster: true }, () =>
      setTimeout(() => {
        this.setState({
          alertMessage: '',
          alertType: '',
          alertToaster: false,
        });
      }, 2000),
    );

  showAlert = (alertType, alertMessage) =>
    void this.setState(
      {
        alertMessage,
        alertType,
        alertToaster: true,
      },
      () => {
        this.toggleAlert();
      },
    );

  validateName = ({ name, id }) => {
    const { clientTagsList } = this.props;

    const isValid = clientTagsList.some(
      (clientTag) => fromAnyToString(name) !== '' && clientTag.id !== id && equalStrings(clientTag.name, name),
    );

    return !isValid;
  };

  render() {
    const { calendarMode, clientTagsList, t } = this.props;
    const { alertMessage, alertToaster, alertType, menuIsClicked, unsavedChangesModal } = this.state;

    const addTagsLayoutClasses = cn({
      'salon settings': true,
      'light-class': calendarMode === 'light',
      'dark-class': calendarMode === 'dark',
      'bright-class': calendarMode !== 'light' && calendarMode !== 'dark',
    });

    return (
      <>
        <SalonFlag name={SF.clientTags}>
          <SalonFlag.IfDisabled>
            <ClientTagsSplashPage />
          </SalonFlag.IfDisabled>
          <SalonFlag.IfEnabled>
            <div data-bem="AddTagsLayout" className={addTagsLayoutClasses}>
              <header data-bem="AddTagsLayout__header" className="settings-header stock-global-nav">
                <div
                  data-bem="AddTagsLayout__sidebarMenu-wrapper"
                  role="button"
                  tabIndex={0}
                  onClick={this.handleMenuClick}
                >
                  <SidebarMenu showMenu={menuIsClicked} isInClientTags />
                </div>

                <div className="global-nav">
                  <div className="page-name text-mobile client-tags" />
                  <nav className="menu-list">
                    <ul className="ul-scroll-menu" />
                  </nav>
                </div>
                <PadLock />
              </header>
              <div
                data-bem="AddTagsLayout__client-tags"
                className="online-booking-layout"
                style={{ marginTop: '80px' }}
              >
                <GeneralTitle generalTitle={t('clientTags.title')} />
                <SubTitle darkTheme={calendarMode === 'dark'} />

                <div data-bem="AddTagsLayout__client-list" className="create-tags-page">
                  {clientTagsList &&
                    clientTagsList.map(
                      ({ id, name, emoticon, description, number_of_clients: numberOfClients }, index) => {
                        const key = id || index;
                        return (
                          <CustomClientTagContainer
                            key={key}
                            description={description}
                            emoticon={emoticon}
                            id={key}
                            name={name}
                            numberOfClients={numberOfClients}
                            setUnsavedChanges={this.setUnsavedChanges}
                            showAlert={this.showAlert}
                            validateName={this.validateName}
                          />
                        );
                      },
                    )}
                  <button className="btn btn-blue" onClick={this.onClickAddNew}>
                    {t('clientTags.addTagButton')}
                  </button>
                </div>
              </div>
              {alertToaster && <div className={`alert centered ${alertType} animated fadeInDown`}>{alertMessage}</div>}
              {unsavedChangesModal && (
                <ConfirmationDialog
                  isOpen={unsavedChangesModal}
                  leftButtonText="Exit Anyway"
                  onLeft={this.onExitPage}
                  onRight={this.onStayToEdit}
                  rightButtonText="Ok"
                  title="You have unsaved changes"
                >
                  <p>Please press SAVE TAG or Undo changes before leaving the page.</p>
                </ConfirmationDialog>
              )}
            </div>
          </SalonFlag.IfEnabled>
        </SalonFlag>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  calendarMode: selectCurrentSalonCalendarMode(state),
  clientTagsList: selectClientTagsFromSalon(state),
  salonId: selectCurrentSalonId(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchSalonTagsList,
      saveSalonTag,
      updateClientTagsList,
    },
    dispatch,
  );

const AddTagsLayoutHoc = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(AddTagsLayout));

export default AddTagsLayoutHoc;
